import { useState } from 'react';
import { MapPin, Loader2, Wifi, Sun, Zap, Wind, Inbox, Link2, Unlink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SearchInput } from '@/components/ui/search-input';
import { Checkbox } from '@/components/ui/checkbox';
import { useSystemDevices, type SystemDevice, type DeviceType } from '@/hooks/useSystemDevices';
import { UnitPickerDrawer } from '@/components/settings/UnitPickerDrawer';
import { useRBAC } from '@/contexts/RBACContext';
import { DeviceIntegrityPanel } from '@/components/settings/DeviceIntegrityPanel';

const COLUMNS: {
  key: DeviceType;
  label: string;
  color: string;
  nameField: (d: SystemDevice) => string;
  secondaryField?: (d: SystemDevice) => string | null;
  secondaryLabel?: string;
  nameLabel: string;
  mono: boolean;
  emptyIcon: typeof Wifi;
  emptyHint?: string;
}[] = [
  {
    key: 'starlink',
    label: 'Starlink',
    color: '#4ade80',
    nameField: (d) => d.service_line_nickname || d.external_terminal_id || '—',
    nameLabel: 'Name',
    mono: false,
    emptyIcon: Wifi,
  },
  {
    key: 'solark',
    label: 'Sol-Ark',
    color: '#f59e0b',
    nameField: (d) => d.solark_name || 'Unnamed plant',
    secondaryField: (d) => d.external_inverter_id || null,
    secondaryLabel: 'Plant ID',
    nameLabel: 'Name',
    mono: false,
    emptyIcon: Sun,
  },
  {
    key: 'omni',
    label: 'GPS',
    color: '#a78bfa',
    nameField: (d) => d.external_device_name || d.external_device_id || '—',
    nameLabel: 'Name',
    mono: false,
    emptyIcon: MapPin,
    emptyHint: 'Run discovery to sync',
  },
  {
    key: 'tesla',
    label: 'Tesla',
    color: '#3b82f6',
    nameField: (d) => d.external_charger_id || '—',
    nameLabel: 'Name',
    mono: true,
    emptyIcon: Zap,
  },
  {
    key: 'hvac',
    label: 'HVAC',
    color: '#06b6d4',
    nameField: (d) => d.external_device_id || '—',
    nameLabel: 'Name',
    mono: false,
    emptyIcon: Wind,
  },
];

function getStatusColor(device: SystemDevice): string {
  if (device.connection_status === 'online') return '#4ade80';
  if (device.connection_status === 'offline') return '#ef4444';

  if (!device.last_seen_at) return '#3f3f46';
  const ageMs = Date.now() - new Date(device.last_seen_at).getTime();
  if (ageMs < 15 * 60 * 1000) return '#4ade80';
  if (ageMs < 60 * 60 * 1000) return '#f59e0b';
  return '#ef4444';
}

function columnSummary(devices: SystemDevice[], allDevices: SystemDevice[]): string {
  const linked = allDevices.filter(d => d.unit_id).length;
  const unlinked = allDevices.filter(d => !d.unit_id).length;
  const parts: string[] = [];
  if (linked > 0) parts.push(`${linked} linked`);
  if (unlinked > 0) parts.push(`${unlinked} unlinked`);
  return parts.join(' · ') || '—';
}

export function SystemDevices() {
  const { isSuperadmin } = useRBAC();
  const {
    devices,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    showUnlinkedOnly,
    setShowUnlinkedOnly,
    filteredDevices,
    refresh,
  } = useSystemDevices();


  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<{
    id: string;
    name: string;
    externalId: string | null;
    type: DeviceType;
    unitId: string | null;
    unitCode: string | null;
  } | null>(null);

  const handleRowClick = (device: SystemDevice, type: DeviceType, nameField: (d: SystemDevice) => string) => {
    setSelectedDevice({
      id: device.id,
      name: nameField(device),
      externalId: type === 'solark' ? device.external_inverter_id || null : null,
      type,
      unitId: device.unit_id,
      unitCode: device.unit_code,
    });
    setDrawerOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 border border-border rounded-lg bg-card/50">
        <Inbox className="w-12 h-12 text-muted-foreground/50 mb-4" />
        <div className="text-muted-foreground text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">System Devices</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Manage hardware devices across all units — link, unlink, and monitor status.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-3">
        <div className="relative w-full sm:w-[280px]">
          <SearchInput
            placeholder="Search devices, serials, units..."
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="bg-card border-border h-9"
          />
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground select-none">
            <Checkbox
              checked={showUnlinkedOnly}
              onCheckedChange={(v) => setShowUnlinkedOnly(v === true)}
            />
            Unlinked only
          </label>
        </div>
      </div>

      {isSuperadmin && <DeviceIntegrityPanel />}

      {/* Horizontal scroll on narrow viewports - 5 device columns need a
          minimum width to stay readable */}
      <div className="border border-border rounded-lg overflow-x-auto bg-card">
        <div className="grid grid-cols-[repeat(5,minmax(max-content,1fr))] min-w-[880px]">
          {COLUMNS.map((col, colIndex) => {
            const deviceList = filteredDevices?.[col.key] || [];
            const allDeviceList = devices?.[col.key] || [];
            const Icon = col.emptyIcon;

            return (
              <div
                key={col.key}
                className={cn(
                  'flex flex-col min-w-0',
                  colIndex < COLUMNS.length - 1 && 'border-r border-border/50'
                )}
              >
                <div className="px-3 py-2.5 text-center border-b border-border bg-card">
                  <span className="text-xs font-semibold">
                    <span
                      className="inline-block w-[7px] h-[7px] rounded-full mr-1.5 align-middle"
                      style={{ background: col.color, position: 'relative', top: '-0.5px' }}
                    />
                    {col.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground ml-1">({allDeviceList.length})</span>
                </div>

                <div className="grid grid-cols-[22px_1fr_auto] gap-x-2 px-2 py-1 border-b border-border text-[10px] font-medium text-muted-foreground/70 uppercase tracking-widest">
                  <span />
                  <span>{col.nameLabel}</span>
                  <span className="text-right">Linked</span>
                </div>

                {deviceList.length > 0 ? (
                  <div className="flex-1 overflow-y-auto">
                    {deviceList.map((device) => {
                      const statusColor = getStatusColor(device);
                      const isUnlinked = !device.unit_id;
                      const deviceName = col.nameField(device);
                      const secondaryValue = col.secondaryField?.(device);
                      return (
                        <div
                          key={device.id}
                          onClick={() => handleRowClick(device, col.key, col.nameField)}
                          className={cn(
                            'grid grid-cols-[22px_1fr_auto] gap-x-2 px-2 py-1.5 border-b border-border/30 items-center cursor-pointer transition-colors',
                            isUnlinked ? 'bg-amber-500/[0.03] hover:bg-amber-500/[0.06]' : 'hover:bg-white/[0.03]'
                          )}
                        >
                          <div className="flex justify-center">
                            <div
                              className="w-1.5 h-1.5 rounded-full"
                              style={{
                                background: statusColor,
                                boxShadow: statusColor === '#4ade80' ? '0 0 4px rgba(74,222,128,0.3)' : undefined,
                              }}
                            />
                          </div>
                          {/* Name and secondary id share ONE line so every row is the
                              same single-row height across all columns; the label
                              ("Plant ID") lives in the tooltip instead of the cell. */}
                          <div className="min-w-0 pr-1 flex items-baseline gap-1.5">
                            <div
                              title={deviceName}
                              className={cn(
                                'text-xs whitespace-nowrap',
                                col.mono ? 'font-mono text-zinc-300' : 'text-foreground',
                                isUnlinked && 'text-zinc-400'
                              )}
                            >
                              {deviceName}
                            </div>
                            {/* Multi-inverter plants (e.g. WEA001TCA) get a compact
                                ×N marker beside the name — same flex row, so the
                                single-line layout holds. Absent/1 renders nothing. */}
                            {col.key === 'solark' && (device.inverter_count ?? 0) > 1 && (
                              <span
                                title={`${device.inverter_count} physical inverters in this plant`}
                                className="text-[9px] text-muted-foreground shrink-0"
                              >
                                ×{device.inverter_count}
                              </span>
                            )}
                            {secondaryValue && (
                              <span
                                title={`${col.secondaryLabel}: ${secondaryValue}`}
                                className="text-[9px] text-muted-foreground font-mono flex-1 text-center whitespace-nowrap"
                              >
                                {col.secondaryLabel}: {secondaryValue}
                              </span>
                            )}
                          </div>
                          <div className="flex justify-end pr-1">
                            {device.unit_code ? (
                              <span title={`Linked to ${device.unit_code}`} aria-label={`Linked to ${device.unit_code}`}>
                                <Link2 className="w-3 h-3 text-[#8FB560]" />
                              </span>
                            ) : (
                              <span title="Not linked" aria-label="Not linked">
                                <Unlink className="w-3 h-3 text-zinc-600" />
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-zinc-700">
                    <Icon className="w-5 h-5 mb-1.5 opacity-40" />
                    <span className="text-[11px]">No devices</span>
                    {col.emptyHint && (
                      <span className="text-[10px] mt-0.5 opacity-50">{col.emptyHint}</span>
                    )}
                  </div>
                )}

                <div className="px-3 py-1.5 text-center text-[10px] text-muted-foreground border-t border-border bg-card">
                  {allDeviceList.length > 0 ? columnSummary(deviceList, allDeviceList) : '—'}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedDevice && (
        <UnitPickerDrawer
          open={drawerOpen}
          onClose={() => { setDrawerOpen(false); setSelectedDevice(null); }}
          deviceId={selectedDevice.id}
          deviceName={selectedDevice.name}
          deviceExternalId={selectedDevice.externalId}
          deviceType={selectedDevice.type}
          currentUnitId={selectedDevice.unitId}
          currentUnitCode={selectedDevice.unitCode}
          onUpdated={refresh}
        />
      )}
    </div>
  );
}
